'use client';

import * as React from 'react';
import type { LocalVideoTrack } from 'livekit-client';

type PrejoinVideoPreviewProps = {
  videoTrack?: LocalVideoTrack;
  videoEnabled: boolean;
  facingMode?: string;
  className?: string;
  /** Espelhar preview (câmera frontal sem fundo virtual). */
  mirror?: boolean;
};

/**
 * Preview de câmera no lobby — attach estável; o elemento <video> permanece montado
 * enquanto a câmera está ligada (evita loop attach/play do LiveKit).
 */
export function PrejoinVideoPreview({
  videoTrack,
  videoEnabled,
  facingMode,
  className = 'w-full h-full object-cover',
  mirror = false,
}: PrejoinVideoPreviewProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const attachedTrackRef = React.useRef<LocalVideoTrack | null>(null);

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoTrack || !videoEnabled) {
      return;
    }

    if (attachedTrackRef.current !== videoTrack) {
      attachedTrackRef.current?.detach(el);
      videoTrack.attach(el);
      attachedTrackRef.current = videoTrack;
    }

    return () => {
      if (attachedTrackRef.current === videoTrack) {
        videoTrack.detach(el);
        attachedTrackRef.current = null;
      }
    };
  }, [videoTrack, videoEnabled]);

  if (!videoEnabled) {
    return null;
  }

  const showLoading = !videoTrack;

  return (
    <div className="absolute inset-0 z-[1]">
      <video
        ref={videoRef}
        className={`absolute inset-0 h-full w-full ${className} ${showLoading ? 'opacity-0' : 'opacity-100'}`}
        style={mirror ? { transform: 'scaleX(-1)' } : undefined}
        autoPlay
        playsInline
        muted
        data-lk-facing-mode={facingMode}
      />
      {showLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0f0f1e]">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/70"
            aria-hidden="true"
          />
          <p className="text-sm text-white/40">Carregando câmera...</p>
        </div>
      )}
    </div>
  );
}
